import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useGoogleLogin } from '@react-oauth/google';
import AppleSignin from 'react-apple-signin-auth';

// 不再动态导入OAuth库
// 注意：如果需要使用@react-oauth/google的hooks，应该在代码中额外添加GoogleOAuthProvider

interface ThirdPartyLoginProps {
  disabled?: boolean;
  forRegister?: boolean;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  agreementAccepted?: boolean;
  onAgreementError?: () => void;
}

export function ThirdPartyLogin({
  disabled = false,
  forRegister = false,
  variant = 'outline',
  size = 'default',
  className = '',
  agreementAccepted = false,
  onAgreementError
}: ThirdPartyLoginProps) {
  const { t } = useTranslation();
  const { loginWithThirdParty } = useAuth();
  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({
    google: false,
    apple: false
  });

  // 处理第三方登录过程中的错误
  const handleLoginError = (provider: string, error: any) => {
    console.error(`${provider} Login Error:`, error);
    toast.error(`${provider} Login Failed`, {
      description: error instanceof Error ? error.message : "Authentication failed",
    });
    setIsLoading(prev => ({ ...prev, [provider.toLowerCase()]: false }));
  };

  // 检查协议是否同意
  const checkAgreement = (): boolean => {
    if (!agreementAccepted) {
      toast.error("Terms not accepted", {
        description: "You must agree to our terms and conditions to continue"
      });
      if (onAgreementError) {
        onAgreementError();
      }
      return false;
    }
    return true;
  };

  // Google 登录 - 使用授权码流程
  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (codeResponse) => {
      try {
        console.log('Google Auth Code:', codeResponse.code);

        // 如果不在登录页面，保存当前位置用于登录后重定向
        if (window.location.pathname !== '/login') {
          sessionStorage.setItem('authRedirectTo', window.location.pathname);
        }

        // 将 Google 返回的授权码传递给服务器，同时传递重定向 URI
        const redirectUri = import.meta.env.VITE_GOOGLE_REDIRECT_URI ||window.location.origin;

        // 检查是否使用的是私有IP地址，如果是，可能需要添加设备参数
        const isPrivateIP = window.location.hostname.match(/^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.|127\.0\.0\.1)/);

        let finalRedirectUri = redirectUri;
        if (isPrivateIP) {
          // 创建设备参数，对于私有IP地址Google需要这些参数
          const deviceId = `anzir_dev_${Math.random().toString(36).substring(2, 10)}`;
          const deviceName = `Anzir_Web_${navigator.platform}`;
          finalRedirectUri = `${redirectUri}?device_id=${encodeURIComponent(deviceId)}&device_name=${encodeURIComponent(deviceName)}`;
          console.log('Using redirect URI with device params:', finalRedirectUri);
        }

        // 登录并获取用户信息 - 注意这里等待函数完成
        try {
          await loginWithThirdParty('google', codeResponse.code, undefined, finalRedirectUri);
          console.log('Google login successful');

          // Google登录成功后，标记可能需要检查用户资料完整性
          // 虽然Google通常提供更多用户信息，但有时也可能不完整
          sessionStorage.setItem('checkProfileCompleteness', 'google');
          
          toast.success(`Google Login Successful`, {
            description: 'You have successfully logged in.',
          });
        } catch (loginError) {
          console.error('Google login error:', loginError);
          throw new Error(loginError instanceof Error ? loginError.message : 'Google login failed');
        }

        // 重定向处理由auth context内部完成
      } catch (error) {
        handleLoginError('Google', error);
      } finally {
        setIsLoading(prev => ({ ...prev, google: false }));
      }
    },
    onError: (error) => {
      handleLoginError('Google', error);
    }
  });

  // Apple 登录回调
  const onAppleSignInSuccess = async (response: any) => {
    try {
      console.log('Apple Sign In Success:', response);

      // 如果不在登录页面，保存当前位置用于登录后重定向
      if (window.location.pathname !== '/login') {
        sessionStorage.setItem('authRedirectTo', window.location.pathname);
      }

      // 获取 authorization.code 和 id_token
      const authCode = response.authorization?.code;
      const idToken = response.authorization?.id_token;

      if (authCode) {
        // 传递 authCode、idToken 和重定向 URI
        const redirectUri = import.meta.env.VITE_APPLE_REDIRECT_URI || window.location.origin;
        
        // 登录并获取用户信息 - 注意这里等待函数完成
        try {
          await loginWithThirdParty('apple', authCode, idToken, redirectUri);
          console.log('Apple login successful');

          // Apple登录成功后，标记可能需要检查用户资料完整性
          // Apple通常不会提供完整的用户信息
          sessionStorage.setItem('checkProfileCompleteness', 'apple');
          
          toast.success(`Apple Login Successful`, {
            description: 'You have successfully logged in.',
          });
        } catch (loginError) {
          console.error('Apple login error:', loginError);
          throw new Error(loginError instanceof Error ? loginError.message : 'Apple login failed');
        }

        // 重定向处理由auth context内部完成
      } else {
        throw new Error('Apple login did not return a valid authorization code');
      }
    } catch (error) {
      handleLoginError('Apple', error);
    } finally {
      setIsLoading(prev => ({ ...prev, apple: false }));
    }
  };

  const onAppleSignInError = (error: any) => {
    handleLoginError('Apple', error);
  };

  const handleGoogleLoginClick = () => {
    if (!checkAgreement()) return;
    
    setIsLoading(prev => ({ ...prev, google: true }));
    try {
      googleLogin();
    } catch (error) {
      handleLoginError('Google', error);
    }
  };

  // 渲染分隔线和标题
  const renderDivider = () => (
    <div className="relative">
      <div className="absolute inset-0 flex items-center">
        <span className="w-full border-t border-white/10" />
      </div>
      <div className="relative flex justify-center text-xs">
        <span className="px-4 py-2 bg-black/60 text-gray-400 font-medium">
          {forRegister ? t("auth.signUpWith", "Sign up with") : t("auth.continueWith", "Continue with")}
        </span>
      </div>
    </div>
  );

  // 渲染 Google 按钮
  const renderGoogleButton = () => (
    <Button
      variant={variant}
      size={size}
      type="button"
      className={`flex items-center justify-center gap-2 w-full bg-background hover:bg-accent/50`}
      onClick={handleGoogleLoginClick}
      disabled={disabled || isLoading.google}
    >
      {isLoading.google ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <svg width="20" height="20" viewBox="0 0 48 48">
          <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
          <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
          <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
          <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
        </svg>
      )}
      <span className="text-sm font-medium ml-2">
        {forRegister ? t("auth.signUpWithGoogle", "Sign up with Google") : t("auth.continueWithGoogle", "Google")}
      </span>
    </Button>
  );

  // 渲染 Apple 按钮
  const renderAppleButton = () => (
    <div className={`relative ${isLoading.apple ? 'pointer-events-none' : ''}`}>
      {isLoading.apple && (
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-background/50 rounded-md">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}

      <AppleSignin
        authOptions={{
          clientId: import.meta.env.VITE_APP_CLIENT_APPLE || '',
          scope: import.meta.env.VITE_APPLE_SCOPE || 'name email',
          redirectURI: import.meta.env.VITE_APPLE_REDIRECT_URI || `${window.location.origin}/auth/apple/callback`,
          state: JSON.stringify({ provider: 'apple', timestamp: Date.now() }),
          nonce: `nonce_${Date.now()}`,
          usePopup: true
        }}
        uiType="dark"
        className="h-11 w-full"
        onSuccess={onAppleSignInSuccess}
        onError={onAppleSignInError}
        render={renderProps => (
          <Button
            variant={variant}
            size={size}
            type="button"
            className="flex items-center justify-center gap-2 w-full bg-background hover:bg-accent/50"
            onClick={() => {
              if (!checkAgreement()) return;
              setIsLoading(prev => ({ ...prev, apple: true }));
              renderProps.onClick();
            }}
            disabled={disabled || isLoading.apple}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.988 3.922-.988 1.831 0 2.35.988 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.597 1.09z" fill="currentColor" />
              <path d="M14.128 3.973c.834-1.014 1.392-2.427 1.235-3.83-1.183.052-2.632.793-3.466 1.78-.742.87-1.392 2.244-1.221 3.57 1.34.104 2.688-.677 3.452-1.52z" fill="currentColor" />
            </svg>
            <span className="text-sm font-medium ml-2">
              {forRegister ? t("auth.signUpWithApple", "Sign up with Apple") : t("auth.continueWithApple", "Apple")}
            </span>
          </Button>
        )}
      />
    </div>
  );

  return (
    <div className={`space-y-6 ${className}`}>
      {renderDivider()}

      <div className="grid grid-cols-2 gap-4">
        {renderGoogleButton()}
        {renderAppleButton()}
      </div>
    </div>
  );
}
